import { useState } from 'react'
import { Button, Input, Modal, Textarea, toast } from '@/components/ui'
import { useCreateDispatch, useSetDispatchLines, useUpdateDispatch } from '../api/shipping-docs-queries'
import type { Dispatch, LineBalance } from '../api/shipping-docs-types'

export interface DispatchFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  odooPoId: number
  /** Present = edit, absent = create. */
  dispatch?: Dispatch | null
  /** The PO's lines with ordered / dispatched / open quantities. */
  balances?: LineBalance[]
}

const num = (v: string | number) => Number(v) || 0
const trim = (n: number) => String(Math.round(n * 10000) / 10000)

/** Create or edit a dispatch. Parent remounts with a new `key` per opening. */
export function DispatchFormModal({ open, onOpenChange, odooPoId, dispatch, balances = [] }: DispatchFormModalProps) {
  const create = useCreateDispatch()
  const update = useUpdateDispatch()
  const setLines = useSetDispatchLines()
  const [label, setLabel] = useState(dispatch?.label ?? '')
  const [shipDate, setShipDate] = useState(dispatch?.ship_date ?? '')
  const [etaDate, setEtaDate] = useState(dispatch?.eta_date ?? '')
  const [reference, setReference] = useState(dispatch?.reference ?? '')
  const [carrier, setCarrier] = useState(dispatch?.carrier ?? '')
  const [notes, setNotes] = useState(dispatch?.notes ?? '')
  const own = new Map((dispatch?.lines ?? []).map((l) => [l.odoo_po_line_id, l]))
  const [qty, setQty] = useState<Record<number, string>>(() =>
    Object.fromEntries((dispatch?.lines ?? []).map((l) => [l.odoo_po_line_id, trim(num(l.quantity))])),
  )
  const pending = create.isPending || update.isPending || setLines.isPending
  /** What this dispatch may still take of a line: the open balance plus what it already carries. */
  const room = (b: LineBalance) => num(b.open) + num(own.get(b.odoo_po_line_id)?.quantity ?? 0)

  const submit = () => {
    const body = {
      label: label.trim() || undefined,
      ship_date: shipDate || null,
      eta_date: etaDate || null,
      reference: reference.trim() || null,
      carrier: carrier.trim() || null,
      notes: notes.trim() || null,
    }
    const lines = balances
      .map((b) => ({ b, q: num(qty[b.odoo_po_line_id] ?? '') }))
      .filter(({ q }) => q > 0)
    const over = lines.find(({ b, q }) => q - room(b) > 0.0001)
    if (over) {
      toast.danger('Too much', { description: `${over.b.product_name}: only ${trim(room(over.b))} left to ship.` })
      return
    }
    const lineInput = lines.map(({ b, q }) => ({ odoo_po_line_id: b.odoo_po_line_id, quantity: q }))
    const fail = (err: unknown) => toast.danger('Save failed', { description: (err as Error).message })
    const finished = () => {
      toast.success(dispatch ? 'Dispatch updated' : 'Dispatch added')
      onOpenChange(false)
    }
    if (dispatch) {
      update.mutate(
        { dispatchId: dispatch.id, body },
        {
          onSuccess: () => {
            if (lineInput.length === 0 && own.size === 0) return finished()
            setLines.mutate({ dispatchId: dispatch.id, lines: lineInput }, { onSuccess: finished, onError: fail })
          },
          onError: fail,
        },
      )
    } else {
      create.mutate({ odooPoId, body: { ...body, lines: lineInput.length ? lineInput : undefined } }, { onSuccess: finished, onError: fail })
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={dispatch ? `Edit ${dispatch.label}` : 'Add dispatch'}
      description="A dispatch is one vendor shipment (container or load). Documents can be filed before Odoo has a receipt for it."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={pending}>
            Save
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-md font-semibold text-primary">Name</span>
          <Input aria-label="Name" value={label} maxLength={128} placeholder="Dispatch 1" onChange={(e) => setLabel(e.target.value)} />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-md font-semibold text-primary">Ship date</span>
            <Input type="date" aria-label="Ship date" value={shipDate} onChange={(e) => setShipDate(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-md font-semibold text-primary">ETA</span>
            <Input type="date" aria-label="ETA" value={etaDate} onChange={(e) => setEtaDate(e.target.value)} />
          </label>
        </div>
        <label className="flex flex-col gap-1.5">
          <span className="text-md font-semibold text-primary">Reference (vessel, booking, BL, container)</span>
          <Input aria-label="Reference" value={reference} maxLength={255} onChange={(e) => setReference(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-md font-semibold text-primary">Carrier</span>
          <Input aria-label="Carrier" value={carrier} maxLength={128} onChange={(e) => setCarrier(e.target.value)} />
        </label>
        {balances.length > 0 ? (
          <fieldset className="flex flex-col gap-2 rounded-md border border-default p-3">
            <legend className="px-1 text-md font-semibold text-primary">What this dispatch carries</legend>
            <p className="text-sm text-secondary">
              Enter the quantity of each line in this shipment. Filing its bill of lading confirms just these
              quantities and leaves the rest open. Leave everything blank if the whole PO ships together.
            </p>
            {balances.map((b) => {
              const locked = own.get(b.odoo_po_line_id)?.confirmed ?? false
              return (
                <div key={b.odoo_po_line_id} className="grid grid-cols-[1fr_auto_8rem_auto] items-center gap-2">
                  <span className="truncate text-md">{b.product_name}</span>
                  <span className="text-sm text-secondary">
                    {trim(room(b))} of {trim(num(b.ordered))} {b.uom ?? ''} open
                  </span>
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    aria-label={`Quantity of ${b.product_name}`}
                    value={qty[b.odoo_po_line_id] ?? ''}
                    disabled={locked}
                    title={locked ? 'Confirmed by a bill of lading — cancel the dispatch to release it' : undefined}
                    onChange={(e) => setQty((q) => ({ ...q, [b.odoo_po_line_id]: e.target.value }))}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={locked || room(b) <= 0}
                    onClick={() => setQty((q) => ({ ...q, [b.odoo_po_line_id]: trim(room(b)) }))}
                  >
                    All
                  </Button>
                </div>
              )
            })}
          </fieldset>
        ) : null}
        <label className="flex flex-col gap-1.5">
          <span className="text-md font-semibold text-primary">Notes</span>
          <Textarea aria-label="Notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>
    </Modal>
  )
}
